import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { themeAPI, authAPI } from '@/utils/api';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Palette, Check, Key, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Loader }from '@/components/ui/loader';
import api from '@/utils/api';

export const AdminSettings = () => {
  const navigate = useNavigate();
  const { theme: currentTheme, refreshTheme } = useTheme();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customTheme, setCustomTheme] = useState({
    preset_name: '',
    primary_color: '#0B1C2D',
    secondary_color: '#C6A24F',
    background_color: '#FFFFFF',
    text_color: '#F9FAFB'
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const fetchThemes = useCallback(async () => {
    try {
      const res = await themeAPI.getAll();
      setThemes(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching themes:', error);
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      await authAPI.getMe();
      fetchThemes();
    } catch (error) {
      navigate('/admin/login');
    }
  }, [navigate, fetchThemes]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleActivateTheme = async (presetName) => {
    const name = presetName.trim();
    try {
      console.log('activating theme', name);
      await themeAPI.activate(name);
      toast.success('Theme activated! Refreshing...');
      await refreshTheme();
      fetchThemes();
    } catch (error) {
      console.error('activate error', error.response || error);
      toast.error(error.response?.data?.detail || 'Error activating theme');
    }
  };

  const handleCreateCustomTheme = async (e) => {
    e.preventDefault();
    
    // require a nonempty name as well
    if (!customTheme.preset_name.trim()) {
      toast.error('Theme name cannot be empty');
      return;
    }

    const hexRegex = /^#[0-9A-F]{6}$/i;
    if (!hexRegex.test(customTheme.primary_color) || 
        !hexRegex.test(customTheme.secondary_color) ||
        !hexRegex.test(customTheme.background_color) ||
        !hexRegex.test(customTheme.text_color)) {
      toast.error('Invalid color format. Use hex format (e.g., #FFFFFF)');
      return;
    }
    
    try {
      const res = await themeAPI.create(customTheme);
      toast.success('Custom theme created! Theme activated automatically.');
      // after creation the backend marks new theme active
      await refreshTheme();
      fetchThemes();
      // clear form
      setCustomTheme({
        preset_name: '',
        primary_color: '#0B1C2D',
        secondary_color: '#C6A24F',
        background_color: '#FFFFFF',
        text_color: '#F9FAFB'
      });
    } catch (error) {
      console.error('Theme creation failed', error.response || error);
      toast.error(error.response?.data?.detail || 'Error creating theme');
    }
  };

  const handleDeleteTheme = async (presetName) => {
    if (window.confirm('Delete this theme?')) {
      try {
        await themeAPI.delete(presetName);
        toast.success('Theme deleted');
        fetchThemes();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error deleting theme');
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await api.post('/admin/change-password', passwordForm);
      toast.success('Password changed! Please login again.');
      localStorage.removeItem('authToken');
      setTimeout(() => navigate('/admin/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error changing password');
    }
  };

  const presetThemes = themes.filter(t => t.is_preset);
  const customThemes = themes.filter(t => !t.is_preset);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader className="text-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 admin-panel">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Back
                </Button>
              </Link>
              <h1 className="text-2xl font-heading font-bold">Settings</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Themes List */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Palette size={24} />
              Available Themes
            </h2>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-600 mb-3">PRESET THEMES</h3>
              <div className="space-y-4">
                {presetThemes.map((theme) => (
                  <div
                    key={theme.preset_name}
                    className={`border rounded-lg p-4 ${
                      theme.is_active ? 'border-green-500 bg-green-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          {theme.preset_name}
                          {theme.is_active && (
                            <span className="inline-flex items-center px-2 py-1 bg-green-500 text-white text-xs rounded">
                              <Check size={12} className="mr-1" />
                              Active
                            </span>
                          )}
                        </h3>
                      </div>
                      {!theme.is_active && (
                        <Button size="sm" onClick={() => handleActivateTheme(theme.preset_name)}>
                          Activate
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <div
                          className="w-full h-12 rounded border"
                          style={{ backgroundColor: theme.primary_color }}
                        ></div>
                        <p className="text-xs text-center mt-1 text-gray-600">Primary</p>
                      </div>
                      <div>
                        <div
                          className="w-full h-12 rounded border"
                          style={{ backgroundColor: theme.secondary_color }}
                        ></div>
                        <p className="text-xs text-center mt-1 text-gray-600">Secondary</p>
                      </div>
                      <div>
                        <div
                          className="w-full h-12 rounded border"
                          style={{ backgroundColor: theme.background_color }}
                        ></div>
                        <p className="text-xs text-center mt-1 text-gray-600">Background</p>
                      </div>
                      <div>
                        <div
                          className="w-full h-12 rounded border"
                          style={{ backgroundColor: theme.text_color }}
                        ></div>
                        <p className="text-xs text-center mt-1 text-gray-600">Text</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-3">
                CUSTOM THEMES ({customThemes.length}/3)
              </h3>
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {customThemes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No custom themes yet</p>
                ) : (
                  customThemes.map((theme) => (
                    <div
                      key={theme.preset_name}
                      className={`border rounded-lg p-4 ${
                        theme.is_active ? 'border-green-500 bg-green-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {theme.preset_name}
                            {theme.is_active && (
                              <span className="inline-flex items-center px-2 py-1 bg-green-500 text-white text-xs rounded">
                                <Check size={12} className="mr-1" />
                                Active
                              </span>
                            )}
                          </h3>
                        </div>
                        <div className="flex gap-2">
                          {!theme.is_active && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleActivateTheme(theme.preset_name)}
                              >
                                Activate
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteTheme(theme.preset_name)}
                                className="text-red-600"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <div
                            className="w-full h-12 rounded border"
                            style={{ backgroundColor: theme.primary_color }}
                          ></div>
                          <p className="text-xs text-center mt-1 text-gray-600">Primary</p>
                        </div>
                        <div>
                          <div
                            className="w-full h-12 rounded border"
                            style={{ backgroundColor: theme.secondary_color }}
                          ></div>
                          <p className="text-xs text-center mt-1 text-gray-600">Secondary</p>
                        </div>
                        <div>
                          <div
                            className="w-full h-12 rounded border"
                            style={{ backgroundColor: theme.background_color }}
                          ></div>
                          <p className="text-xs text-center mt-1 text-gray-600">Background</p>
                        </div>
                        <div>
                          <div
                            className="w-full h-12 rounded border"
                            style={{ backgroundColor: theme.text_color }}
                          ></div>
                          <p className="text-xs text-center mt-1 text-gray-600">Text</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Create Theme Form */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">Create Custom Theme</h2>
            <form onSubmit={handleCreateCustomTheme} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Theme Name *</label>
                <Input
                  value={customTheme.preset_name}
                  onChange={(e) => setCustomTheme({ ...customTheme, preset_name: e.target.value })}
                  required
                  placeholder="e.g., My Custom Theme"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Primary Color *</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={customTheme.primary_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, primary_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={customTheme.primary_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, primary_color: e.target.value })}
                    className="flex-1"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Secondary Color *</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={customTheme.secondary_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, secondary_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={customTheme.secondary_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, secondary_color: e.target.value })}
                    className="flex-1"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Background Color *</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={customTheme.background_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, background_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={customTheme.background_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, background_color: e.target.value })}
                    className="flex-1"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Text Color *</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={customTheme.text_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, text_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={customTheme.text_color}
                    onChange={(e) => setCustomTheme({ ...customTheme, text_color: e.target.value })}
                    className="flex-1"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <p className="text-sm font-medium mb-2">Preview:</p>
                <div
                  className="p-6 rounded"
                  style={{
                    backgroundColor: customTheme.primary_color,
                    color: customTheme.text_color
                  }}
                >
                  <h3 className="text-xl font-bold mb-2">Sample Heading</h3>
                  <p className="mb-3">This is how your text will look.</p>
                  <button
                    type="button"
                    className="px-4 py-2 rounded font-semibold"
                    style={{
                      backgroundColor: customTheme.secondary_color,
                      color: customTheme.primary_color
                    }}
                  >
                    Sample Button
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full">
                Create Theme
              </Button>
            </form>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Key size={24} />
            Change Password
          </h2>
          <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Current Password *</label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                New Password * (min 8 characters)
              </label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
                minLength={8}
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm New Password *</label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
                placeholder="Confirm new password"
              />
            </div>

            <Button type="submit" className="w-full">
              Change Password
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};